import { FC, useMemo } from 'react';
import { DecimalFormat } from '@tempusfinance/decimal';
import { Asset } from '../../../interfaces';
import Icon from '../Icon';
import TooltipWrapper from '../TooltipWrapper';
import Typography, { TypographyVariant } from '../Typography';
import Link from '../Link';
import isMobile from 'is-mobile';

import './AssetApy.scss';

const mobile = isMobile();

interface AssetBoostedApyProps {
  asset: Asset;
  size?: 'regular' | 'small' | 'tiny';
  bold?: boolean;
  disableTooltip?: boolean;
}

const AssetApy: FC<AssetBoostedApyProps> = ({ asset, size = 'regular', bold = false, disableTooltip = false }) => {
  const typographyVariant: TypographyVariant = useMemo(() => {
    switch (size) {
      case 'regular':
        return 'body-lg';
      case 'small':
        return 'body-md';
      case 'tiny':
        return 'body-sm';
    }
  }, [size]);

  const iconSize = useMemo(() => {
    switch (size) {
      case 'regular':
        return 14;
      case 'small':
        return 12;
      case 'tiny':
        return 12;
    }
  }, [size]);

  const formattedApy = useMemo(() => {
    if (!asset.supplyApy) {
      return null;
    }

    return DecimalFormat.format(asset.supplyApy, { style: 'percentage', fractionDigits: 2, pad: true });
  }, [asset.supplyApy]);

  const formattedSupplyApy = useMemo(() => {
    if (!asset.supplyApy || !asset.defiSpringRewardsApy) {
      return null;
    }

    return DecimalFormat.format(asset.supplyApy.sub(asset.defiSpringRewardsApy), {
      style: 'percentage',
      fractionDigits: 2,
      pad: true,
    });
  }, [asset.defiSpringRewardsApy, asset.supplyApy]);

  const formattedRewardsApy = useMemo(() => {
    if (!asset.defiSpringRewardsApy || (asset.defiSpringRewardsApy && asset.defiSpringRewardsApy.isZero())) {
      return null;
    }

    return DecimalFormat.format(asset.defiSpringRewardsApy, { style: 'percentage', fractionDigits: 2, pad: true });
  }, [asset.defiSpringRewardsApy]);

  if (asset.defiSpringRewardsApy && asset.defiSpringRewardsApy.isZero()) {
    return (
      <Typography variant={typographyVariant} color="text-primary" weight={bold ? 'bold' : 'regular'}>
        {formattedSupplyApy}
      </Typography>
    );
  }

  return (
    <div className="nostra__assetBoostedApy">
      <TooltipWrapper
        disabled={disableTooltip}
        placement="bottom-end"
        openEvent={mobile ? 'click' : 'mouseenter'}
        tooltipContent={
          <div className="nostra__tooltipBox">
            <div className="nostra__assetBoostedApy__tooltipRow">
              <Typography variant="table-body-md" color="text-primary-darkbg">
                Supply APY
              </Typography>
              <Typography variant="table-body-md" color="text-primary-darkbg">
                {formattedSupplyApy}
              </Typography>
            </div>
            {formattedRewardsApy && (
              <div className="nostra__assetBoostedApy__tooltipRow" data-rewards={Boolean(formattedRewardsApy)}>
                <Typography variant="table-body-md" color="text-primary-darkbg">
                  DeFi Spring STRK rewards
                </Typography>
                <Typography variant="table-body-md" color="text-primary-darkbg">
                  {formattedRewardsApy}
                </Typography>
              </div>
            )}
            <div
              className="nostra__assetBoostedApy__tooltipNotice"
              onClick={event => {
                event.stopPropagation();
              }}
            >
              <Typography variant="table-body-sm" color="text-supporting-darkbg">
                Supplying and borrowing the same asset, or supplying and borrowing stablecoins including USDC.e, USDT, DAI
                or UNO in the same account, will reduce your DeFi Spring rewards.{' '}
                <Link href="https://docs.nostra.finance/start-here/starknet-defi-spring">Learn more</Link>.
              </Typography>
            </div>
          </div>
        }
      >
        <Icon variant="boost-stars" size={iconSize} />
        <Typography variant={typographyVariant} weight="semi-bold">
          {formattedApy}
        </Typography>
      </TooltipWrapper>
    </div>
  );
};
export default AssetApy;
